import { useFormikContext } from 'formik';
import * as inflection from 'inflection';
import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  ProvisionTypeSelectOption,
  ProvisionTypeSelectSingleValue,
} from '../../admin/provisions/ProvisionTypeSelectOption';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import { Field, FieldSet } from '../../components/Form';
import SVGIcon from '../../components/SVGIcon';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { ProvisionType } from '../../graphql/generated/graphql';
import checkIcon from '../../icons/non-prefilled/16x16/check.svg';
import linkIcon from '../../icons/non-prefilled/16x16/link.svg';
import type { InvitationUserSelectOptionType } from '../../invitation/InvitationUserSelect';
import InvitationUserSelect from '../../invitation/InvitationUserSelect';
import useInviteMutation from '../../invitation/useInviteMutation';
import useIsLayoutSize from '../../post/utils/useIsLayoutSize';
import { LayoutSize } from '../../post/utils/useLayoutSize';
import { invitationToasts, miscToasts } from '../../toasts/toasts';
import { UIActions } from '../../ui/uiSlice';
import handleApolloError from '../../utils/error/handleApolloError';
import provisionRegisterUrl from '../../utils/url/provisionRegisterUrl';
import type { OrganizationOnboardingStepBaseProps } from './OrganizationOnboardingStep';
import OrganizationOnboardingStep from './OrganizationOnboardingStep';
import * as styles from './OrganizationOnboardingStepInvite.module.scss';

const ONBOARDING_STEP_INVITE_PROVISION_FRAGMENT = gql(`
  fragment OnboardingStepInvite_provision on Provision {
    id
    type
    ...provisionRegisterUrl_provision
  }
`);

const ONBOARDING_INVITE_QUERY = gql(/** @complexity 4 */ `
  query OrganizationOnboardingStepInvite {
    session: currentSession {
      organization {
        id
        provisions {
          ...OnboardingStepInvite_provision
        }
      }
    }
  }
`);

type Values = {
  invites: InvitationUserSelectOptionType[];
  type: ProvisionType;
};

interface InviteFieldsProps {
  provisions?: DocumentType<typeof ONBOARDING_STEP_INVITE_PROVISION_FRAGMENT>[];
}

const InviteFields = ({ provisions }: InviteFieldsProps) => {
  const { values, setFieldValue } = useFormikContext<Values>();
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    setIsLinkCopied(false);
  }, [values.type]);

  const selectedProvision = provisions?.find(
    (provision) => provision.type === values.type,
  );
  const currentProvisionsUrl = selectedProvision
    ? provisionRegisterUrl(selectedProvision)
    : null;

  const handleCopyButtonClick = async () => {
    if (currentProvisionsUrl) {
      await navigator.clipboard.writeText(currentProvisionsUrl);
      setIsLinkCopied(true);
      dispatch(UIActions.pushToast(miscToasts.shareableLinkCopied));
    }
  };

  return (
    <>
      <div className={styles.invitesLegend}>
        <div className={styles.invitesLegendText}>To:</div>
        {currentProvisionsUrl && !isLinkCopied && (
          <Button
            className={styles.provisionLink}
            onClick={handleCopyButtonClick}
          >
            <SVGIcon className={styles.linkIcon} size={10}>
              {linkIcon}
            </SVGIcon>
            Copy shareable link
          </Button>
        )}
        {isLinkCopied && (
          <div className={styles.provisionLink}>
            <SVGIcon className={styles.linkIcon} size={10}>
              {checkIcon}
            </SVGIcon>
            Link copied
          </div>
        )}
      </div>
      <FieldSet>
        <InvitationUserSelect
          value={values.invites}
          onChange={(invites) => {
            setFieldValue('invites', invites);
          }}
          placeholder="Enter a name or email of a teammate"
        />
      </FieldSet>
      <Field
        label="Role"
        type="select"
        name="type"
        isClearable={false}
        margin="none"
        options={[
          {
            description: 'Standard team seat',
            value: ProvisionType.User,
          },
          {
            description: 'Unlocks all privileges',
            value: ProvisionType.Admin,
          },
          {
            description: 'Limited access to topics',
            value: ProvisionType.Guest,
          },
        ]}
        components={{
          Option: ProvisionTypeSelectOption,
          SingleValue: ProvisionTypeSelectSingleValue,
        }}
      />
    </>
  );
};

const OrganizationOnboardingStepInvite = (
  props: OrganizationOnboardingStepBaseProps,
) => {
  const dispatch = useAppDispatch();
  const isMobile = useIsLayoutSize(LayoutSize.mobile);

  const { data: { session } = {} } = useQuery(ONBOARDING_INVITE_QUERY, {
    fetchPolicy: 'network-only',
  });

  const provisions = session?.organization.provisions ?? [];

  const invite = useInviteMutation();

  const onSubmit = async ({ invites, type }: Values): Promise<boolean> => {
    const results = await Promise.all(
      invites.map(async ({ value: email, label }) => {
        try {
          await invite({
            variables: {
              email,
              name: label !== email ? label : null,
              type,
            },
          });
          return true;
        } catch (error) {
          handleApolloError(error, {
            USER_EXISTS: () =>
              dispatch(
                UIActions.pushToast(
                  invitationToasts.userExistsWarning({ label }),
                ),
              ),
            USER_INVITED: () =>
              dispatch(
                UIActions.pushToast(
                  invitationToasts.userInvitedWarning({ label }),
                ),
              ),
            RATE_LIMIT: () =>
              dispatch(
                UIActions.pushToast(invitationToasts.rateLimitError({ label })),
              ),
          });
          return false;
        }
      }),
    );

    const sentInvites = invites.filter((_, index) => results[index]);
    const totalSent = sentInvites.length;
    if (totalSent > 0) {
      dispatch(
        UIActions.pushToast(invitationToasts.invitationSent({ totalSent })),
      );
    }
    return totalSent === results.length;
  };

  return (
    <OrganizationOnboardingStep<Values>
      {...props}
      onPrevious={isMobile ? null : props.onPrevious}
      footer={(values) => ({
        submitButton: {
          text: `Invite ${values.invites.length} ${inflection.inflect('teammate', values.invites.length)}`,
          disabled: values.invites.length === 0,
        },
        secondaryButton: {
          text: 'Skip',
          onClick: () => props.onNext(),
        },
      })}
      initialValues={{ invites: [], type: ProvisionType.User }}
      title="Invite teammates"
      onSubmit={onSubmit}
    >
      <InviteFields provisions={provisions} />
    </OrganizationOnboardingStep>
  );
};

export default OrganizationOnboardingStepInvite;
